import { Button } from "@mui/material";
import { useSnackbar } from "notistack";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { server } from "src/coreConfig";
import { useSelector } from "src/store";
import axiosInstance from "src/utils/axios";
import { handleDownloadFile } from "src/utils/dataRenders";

const InvoiceGenerationContext = createContext(null);
const ACTIVE_STATUSES = new Set(["submitting", "queued", "processing"]);
const MAX_WAIT_MS = 15 * 60 * 1000;
const POLL_INTERVAL_MS = 3000;
const STORAGE_PREFIX = "arkadu.invoice-generation-queue.v1";

const urls = {
  request: `${server}/api/v2/dinvoice/invoice/generation/request/`,
  status: `${server}/api/v2/dinvoice/invoice/generation/status/`,
  list: `${server}/api/v2/dinvoice/invoice/generation/list/`,
  retry: `${server}/api/v2/dinvoice/invoice/generation/retry/`,
  download: `${server}/api/v2/dinvoice/invoice/generation/download/`,
};

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback;

export const InvoiceGenerationProvider = ({ children }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { currentCustomer } = useSelector((state) => state.customers);
  const { adminMode } = useSelector((state) => state.settings);
  const { user } = useSelector((state) => state.users);
  const [requests, setRequests] = useState([]);
  const loadedStorageKey = useRef(null);
  const requestsRef = useRef([]);
  const polling = useRef(false);
  const downloading = useRef(new Set());
  const replacing = useRef(new Set());
  const snackbarKey = useRef(null);

  const storageKey = useMemo(() => {
    if (!currentCustomer?.id || !user?.id) return null;
    return `${STORAGE_PREFIX}:${currentCustomer.id}:${user.id}:${adminMode ? "admin" : "user"}`;
  }, [adminMode, currentCustomer?.id, user?.id]);

  const scopedPayload = useCallback(
    (extra = {}) => ({
      customer_id: currentCustomer?.id,
      mode: adminMode ? "admin" : "user",
      ...extra,
    }),
    [adminMode, currentCustomer?.id],
  );

  useEffect(() => {
    loadedStorageKey.current = storageKey;
    if (!storageKey) {
      setRequests([]);
      return;
    }
    try {
      const stored = JSON.parse(localStorage.getItem(storageKey) || "[]");
      setRequests(
        Array.isArray(stored)
          ? stored.filter(
              (request) => !String(request.id).startsWith("request-"),
            )
          : [],
      );
    } catch (_error) {
      setRequests([]);
    }
  }, [storageKey]);

  useEffect(() => {
    requestsRef.current = requests;
    if (storageKey && loadedStorageKey.current === storageKey) {
      localStorage.setItem(storageKey, JSON.stringify(requests));
    }
  }, [requests, storageKey]);

  useEffect(() => {
    if (!storageKey || !currentCustomer?.id || !user?.id) return undefined;
    let cancelled = false;
    axiosInstance
      .post(urls.list, scopedPayload({ limit: 100 }), { timeout: 15000 })
      .then((response) => {
        if (cancelled) return;
        const active = (response.data?.data || []).filter((generation) =>
          ACTIVE_STATUSES.has(generation.status),
        );
        if (!active.length) return;
        setRequests((current) => {
          const knownIds = new Set(current.map((request) => request.id));
          return [
            ...current,
            ...active
              .filter((generation) => !knownIds.has(generation.id))
              .map((generation) => ({
                id: generation.id,
                generation,
                payload: null,
                requestedAt: generation.created
                  ? new Date(generation.created).getTime()
                  : Date.now(),
              })),
          ];
        });
      })
      .catch((error) => {
        console.error("Could not restore invoice generation queue", error);
      });
    return () => {
      cancelled = true;
    };
  }, [currentCustomer?.id, scopedPayload, storageKey, user?.id]);

  const removeRequest = useCallback((id) => {
    setRequests((current) => current.filter((request) => request.id !== id));
  }, []);

  const downloadGeneratedInvoice = useCallback(
    async (generation) => {
      const response = await axiosInstance.post(
        urls.download,
        scopedPayload({ id: generation.id }),
        { responseType: "blob", timeout: 60000 },
      );
      handleDownloadFile(response, generation.filename);
      return generation;
    },
    [scopedPayload],
  );

  const listGeneratedInvoices = useCallback(
    async (limit = 50) => {
      const response = await axiosInstance.post(
        urls.list,
        scopedPayload({ limit }),
      );
      return response.data?.res === 1 ? response.data.data : [];
    },
    [scopedPayload],
  );

  const retryGeneratedInvoice = useCallback(
    async (id) => {
      const response = await axiosInstance.post(
        urls.retry,
        scopedPayload({ id }),
      );
      const generation = response.data?.data;
      if (generation) {
        setRequests((current) => [
          ...current.filter((request) => request.id !== id),
          {
            id: generation.id,
            generation,
            payload: null,
            requestedAt: Date.now(),
          },
        ]);
      }
      return generation;
    },
    [scopedPayload],
  );

  const requestInvoice = useCallback(
    async (data) => {
      const provisionalId = `request-${Date.now()}-${Math.random()}`;
      const payload = scopedPayload({
        instance: data.instance,
        item_id: data.id,
        filename: data.filename || `invoice_${data.id}.pdf`,
        force: data.force || false,
      });
      setRequests((current) => [
        ...current,
        {
          id: provisionalId,
          generation: { status: "submitting", filename: payload.filename },
          payload,
          requestedAt: Date.now(),
        },
      ]);

      try {
        const response = await axiosInstance.post(urls.request, payload, {
          timeout: MAX_WAIT_MS,
        });
        if (response.data?.res !== 1) {
          throw new Error(
            response.data?.message || t("No se pudo generar la planilla"),
          );
        }
        const generation = response.data.data;
        if (generation.download_ready) {
          removeRequest(provisionalId);
          await downloadGeneratedInvoice(generation);
          enqueueSnackbar(t("PDF descargado correctamente"), {
            variant: "success",
          });
          return generation;
        }
        setRequests((current) => [
          ...current.filter((request) => request.id !== provisionalId),
          { id: generation.id, generation, payload, requestedAt: Date.now() },
        ]);
        return generation;
      } catch (error) {
        removeRequest(provisionalId);
        enqueueSnackbar(
          getErrorMessage(error, t("No se pudo solicitar la planilla")),
          { variant: "error" },
        );
        throw error;
      }
    },
    [
      downloadGeneratedInvoice,
      enqueueSnackbar,
      removeRequest,
      scopedPayload,
      t,
    ],
  );

  const pollRequests = useCallback(async () => {
    if (polling.current || !currentCustomer?.id) return;
    const activeRequests = requestsRef.current.filter((request) =>
      ACTIVE_STATUSES.has(request.generation?.status),
    );
    if (!activeRequests.length) return;

    polling.current = true;
    await Promise.all(
      activeRequests.map(async (request) => {
        if (request.id.startsWith("request-")) return;
        if (Date.now() - request.requestedAt > MAX_WAIT_MS) {
          removeRequest(request.id);
          enqueueSnackbar(
            t("La generaci�n excedi� el tiempo de espera. Revise Mis PDF."),
            { variant: "warning" },
          );
          return;
        }

        try {
          const response = await axiosInstance.post(
            urls.status,
            scopedPayload({ id: request.id }),
            { timeout: 15000 },
          );
          const generation = response.data?.data;
          if (!generation) return;

          if (
            generation.download_ready &&
            !downloading.current.has(request.id)
          ) {
            downloading.current.add(request.id);
            try {
              await downloadGeneratedInvoice(generation);
              removeRequest(request.id);
              enqueueSnackbar(t("PDF descargado correctamente"), {
                variant: "success",
              });
            } finally {
              downloading.current.delete(request.id);
            }
            return;
          }

          if (generation.status === "stale" && request.payload) {
            if (replacing.current.has(request.id)) return;
            replacing.current.add(request.id);
            try {
              const replacement = await axiosInstance.post(
                urls.request,
                request.payload,
                { timeout: MAX_WAIT_MS },
              );
              const nextGeneration = replacement.data?.data;
              if (nextGeneration) {
                setRequests((current) => [
                  ...current.filter((item) => item.id !== request.id),
                  {
                    ...request,
                    id: nextGeneration.id,
                    generation: nextGeneration,
                  },
                ]);
              }
            } finally {
              replacing.current.delete(request.id);
            }
            return;
          }

          if (generation.status === "failed") {
            removeRequest(request.id);
            enqueueSnackbar(
              generation.error || t("No se pudo generar el documento"),
              { variant: "error" },
            );
            return;
          }

          setRequests((current) =>
            current.map((item) =>
              item.id === request.id ? { ...item, generation } : item,
            ),
          );
        } catch (error) {
          console.error("Invoice generation polling failed", error);
        }
      }),
    );
    polling.current = false;
  }, [
    currentCustomer?.id,
    downloadGeneratedInvoice,
    enqueueSnackbar,
    removeRequest,
    scopedPayload,
    t,
  ]);

  useEffect(() => {
    pollRequests();
    const timer = window.setInterval(pollRequests, POLL_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [pollRequests]);

  const pendingSummary = useMemo(() => {
    const active = requests.filter((request) =>
      ACTIVE_STATUSES.has(request.generation?.status),
    );
    const processing = active.filter(
      (request) => request.generation?.status === "processing",
    ).length;
    const names = active
      .slice(0, 2)
      .map((request) => request.generation?.filename)
      .filter(Boolean)
      .join(", ");
    return { total: active.length, processing, names };
  }, [requests]);

  useEffect(() => {
    if (snackbarKey.current) {
      closeSnackbar(snackbarKey.current);
      snackbarKey.current = null;
    }
    if (!pendingSummary.total) return undefined;

    snackbarKey.current = enqueueSnackbar(
      pendingSummary.processing
        ? t("Generando {{count}} PDF: {{names}}", {
            count: pendingSummary.total,
            names: pendingSummary.names,
          })
        : t("{{count}} PDF en cola: {{names}}", {
            count: pendingSummary.total,
            names: pendingSummary.names,
          }),
      {
        persist: true,
        variant: "info",
        action: () => (
          <Button
            color="inherit"
            size="small"
            onClick={() => navigate("/app/generated-documents/")}
          >
            {t("Ver cola")}
          </Button>
        ),
      },
    );
    return undefined;
  }, [
    closeSnackbar,
    enqueueSnackbar,
    navigate,
    pendingSummary.processing,
    pendingSummary.names,
    pendingSummary.total,
    t,
  ]);

  const value = useMemo(
    () => ({
      pendingRequests: requests,
      requestInvoice,
      downloadGeneratedInvoice,
      listGeneratedInvoices,
      retryGeneratedInvoice,
    }),
    [
      downloadGeneratedInvoice,
      listGeneratedInvoices,
      requestInvoice,
      requests,
      retryGeneratedInvoice,
    ],
  );

  return (
    <InvoiceGenerationContext.Provider value={value}>
      {children}
    </InvoiceGenerationContext.Provider>
  );
};

export const useInvoiceGeneration = () => {
  const context = useContext(InvoiceGenerationContext);
  if (!context) {
    throw new Error("useInvoiceGeneration must be used inside its provider");
  }
  return context;
};
